import React, { useEffect, useRef, useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import ArrowBack from "@material-ui/icons/ArrowBack";
import Check from "@material-ui/icons/Check";
import FlipCameraAndroid from "@material-ui/icons/FlipCameraAndroid";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import Replay from "@material-ui/icons/Replay";
import logoPreto from "../../assets/images/logo-preto.png";
import exemploDocumento from "../../assets/images/exemplo-documento.webp";
import { useCameraCapture } from "@hooks";
import "./styles.css";

const TIPOS_ACEITOS = "image/jpeg,image/png";

const CapturaFoto = ({
  tipoGuia,
  facingModeInicial,
  instrucao,
  altTextoPreview,
  textoBotaoConfirmar = "Confirmar",
  onConfirmar,
  onVoltarEtapa,
}) => {
  const {
    videoRef,
    foto,
    error,
    uploadErro,
    isCameraFrontal,
    podeAlternarCamera,
    iniciar,
    capturar,
    processarArquivo,
    tirarNovamente,
    parar,
    alternarCamera,
  } = useCameraCapture(facingModeInicial);
  const [modo, setModo] = useState("escolha");
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState(null);
  const [isCameraPronta, setIsCameraPronta] = useState(false);
  const [isFotoEspelhada, setIsFotoEspelhada] = useState(false);
  const inputArquivoRef = useRef(null);
  const isPreviewEspelhado = isCameraFrontal && tipoGuia !== "documento";

  useEffect(() => {
    if (modo === "camera") {
      setIsCameraPronta(false);
      iniciar();
      return () => parar();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo]);

  const handleSelecionarArquivo = (event) => {
    const arquivo = event.target.files?.[0];
    event.target.value = "";
    setIsFotoEspelhada(false);
    processarArquivo(arquivo);
  };

  const handleCapturar = () => {
    setIsFotoEspelhada(isPreviewEspelhado);
    capturar();
  };

  const handleVoltar = () => {
    setErroEnvio(null);
    tirarNovamente();
    setModo("escolha");
  };

  const handleTirarNovamente = () => {
    setErroEnvio(null);
    tirarNovamente();
  };

  const handleConfirmar = async () => {
    setErroEnvio(null);
    setEnviando(true);

    try {
      await onConfirmar(foto);
      tirarNovamente();
      setModo("escolha");
    } catch (erro) {
      setErroEnvio(
        erro?.response?.data?.message ||
          erro?.message ||
          "Não foi possível enviar a foto. Tente novamente.",
      );
    } finally {
      setEnviando(false);
    }
  };

  const handleVoltarEtapa = () => {
    parar();
    setErroEnvio(null);
    tirarNovamente();
    setModo("escolha");
    onVoltarEtapa();
  };

  return (
    <div className="card-container captura-card">
      <div className="card-header">
        <img className="img-logo" src={logoPreto} alt="logo" />
      </div>

      {!foto && (
        <div
          className={onVoltarEtapa ? "captura-topo" : "captura-topo-sem-voltar"}
        >
          {onVoltarEtapa && (
            <IconButton
              edge="start"
              onClick={handleVoltarEtapa}
              aria-label="Voltar para a foto anterior"
              style={{ color: "#000" }}
            >
              <ArrowBack />
            </IconButton>
          )}

          <span
            className={`captura-instrucao${onVoltarEtapa ? "--com-voltar" : "--sem-voltar"}`}
          >
            {instrucao}
          </span>
        </div>
      )}

      {modo === "escolha" && (
        <div className="captura-escolha">
          <div className="captura-placeholder">
            <PhotoCamera />
          </div>
          <div className="captura-acoes">
            <button
              className="button-primary"
              onClick={() => setModo("camera")}
            >
              Tirar foto
            </button>
            <button
              className="button-primary button-secundario"
              onClick={() => setModo("upload")}
            >
              Enviar arquivo
            </button>
          </div>
        </div>
      )}

      {modo === "upload" && !foto && (
        <div className="captura-escolha">
          {uploadErro && <p className="captura-erro">{uploadErro}</p>}
          <div className="captura-acoes">
            <button
              className="button-primary"
              onClick={() => inputArquivoRef.current?.click()}
            >
              Selecionar arquivo
            </button>
            <button
              className="button-primary button-secundario"
              onClick={handleVoltar}
            >
              Voltar
            </button>
          </div>
          <input
            ref={inputArquivoRef}
            type="file"
            accept={TIPOS_ACEITOS}
            style={{ display: "none" }}
            onChange={handleSelecionarArquivo}
          />
          <p className="captura-ajuda">
            Formatos aceitos: JPEG ou PNG. Tamanho máximo: 10MB.
          </p>
        </div>
      )}

      {modo === "camera" && error && (
        <div className="captura-erro">
          <p>
            Não foi possível acessar a câmera. Verifique a permissão do
            navegador.
          </p>
          <div className="captura-acoes">
            <button className="button-primary" onClick={iniciar}>
              Tentar novamente
            </button>
            <button
              className="button-primary button-secundario"
              onClick={handleVoltar}
            >
              Voltar
            </button>
          </div>
        </div>
      )}

      {modo === "camera" && !error && (
        <>
          <div
            className="captura-camera"
            style={{ display: foto ? "none" : "block" }}
          >
            <video
              ref={videoRef}
              className={`captura-preview${isPreviewEspelhado ? " captura-preview--espelhada" : ""}`}
              autoPlay
              playsInline
              muted
              onPlaying={() => setIsCameraPronta(true)}
            />
            {tipoGuia && (
              <div
                className={`captura-guia captura-guia--${tipoGuia}`}
                aria-hidden="true"
              >
                {isCameraPronta && tipoGuia === "documento" && (
                  <img
                    className="captura-exemplo"
                    src={exemploDocumento}
                    alt=""
                  />
                )}
              </div>
            )}
          </div>
          {!foto && (
            <div className="captura-acoes captura-acoes--icones captura-acoes--sobreposta">
              <button
                className="button-primary"
                onClick={handleCapturar}
                aria-label="Tirar foto"
              >
                <PhotoCamera className="captura-acao-icone" />
                <span className="captura-acao-texto">Tirar foto</span>
              </button>
              {podeAlternarCamera && (
                <button
                  className="button-primary button-secundario captura-acao-secundaria"
                  onClick={alternarCamera}
                  aria-label="Trocar câmera"
                >
                  <FlipCameraAndroid className="captura-acao-icone" />
                  <span className="captura-acao-texto">Trocar câmera</span>
                </button>
              )}
            </div>
          )}
        </>
      )}

      {foto && (
        <>
          <div className="captura-camera captura-camera--foto">
            <img
              className={`captura-preview${isFotoEspelhada ? " captura-preview--espelhada" : ""}`}
              src={foto}
              alt={altTextoPreview}
            />
          </div>
          <div className="captura-acoes captura-acoes--icones">
            <button
              className="button-primary button-secundario captura-acao-secundaria"
              onClick={handleTirarNovamente}
              disabled={enviando}
            >
              <Replay className="captura-acao-icone" />
              <span className="captura-acao-texto">Tirar novamente</span>
            </button>
            <button
              className="button-primary"
              onClick={handleConfirmar}
              disabled={enviando}
            >
              <Check className="captura-acao-icone" />
              <span className="captura-acao-texto">
                {enviando ? "Enviando..." : textoBotaoConfirmar}
              </span>
            </button>
          </div>
          {erroEnvio && <p className="captura-erro">{erroEnvio}</p>}
        </>
      )}
    </div>
  );
};

export default CapturaFoto;
